// trash.js — soft-delete / recoverable trash bin for pancake orders.
//
// Orders are never hard-deleted from the UI directly anymore. Instead:
//   1. 🗑 button in /library?tab=shifts calls trashOrder() → sets trashedAt.
//   2. Items in trash are filtered out of every list view (gallery, site,
//      overlays, insights, meditations, etc.) via `!order.trashedAt`.
//   3. A Cloud Function (cleanupTrashedOrders, scheduled daily) permanently
//      deletes orders whose trashedAt is older than TRASH_RETENTION_DAYS,
//      emails Dan an audit digest, and writes each purge to deletedOrderAudit.
//   4. Staff can Restore or Delete Forever from the Trash accordion in
//      /library?tab=shifts.
//
// Hide is different from Trash — `hidden: true` means "don't show publicly";
// `trashedAt` means "scheduled for permanent deletion, recoverable."

import { doc, updateDoc, deleteDoc, serverTimestamp, collection, getDocs, query, where } from "firebase/firestore";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";

export const TRASH_RETENTION_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

// Move an order to trash — sets trashedAt + trashedBy. Reversible via restoreOrder.
export async function trashOrder(orderId, userEmail) {
  await updateDoc(doc(db, "orders", orderId), {
    trashedAt: serverTimestamp(),
    trashedBy: userEmail || null,
  });
}

// Same as trashOrder, but for the shopOrders collection (standalone commissions
// that never produced a linked orders doc). The scheduled cleanup function
// only purges the `orders` collection, so shopOrders stay trashed until
// manually purged — that's fine, they're a small list.
export async function trashShopOrder(orderId, userEmail) {
  await updateDoc(doc(db, "shopOrders", orderId), {
    trashedAt: serverTimestamp(),
    trashedBy: userEmail || null,
  });
}

// Restore an order from trash — clears trashedAt + trashedBy.
export async function restoreOrder(orderId) {
  await updateDoc(doc(db, "orders", orderId), {
    trashedAt: null,
    trashedBy: null,
  });
}

// Hard-delete — removes Storage files, order doc, linked token. No undo.
// Called from inside the Trash view's "Delete forever" button AND by the
// scheduled cleanup Cloud Function.
export async function deleteOrderForever(order) {
  const urls = [order.photo_url, order.photo_cutout_url, order.thumbnailUrl, order.imageUrl, order.croppedImageUrl].filter(Boolean);
  await Promise.allSettled(urls.map(u => deleteObject(storageRef(storage, u)).catch(() => {})));
  await deleteDoc(doc(db, "orders", order.id));
  if (order.tokenId) {
    await deleteDoc(doc(db, "tokens", order.tokenId)).catch(() => {});
  }
}

// Days remaining before an order is auto-purged. Returns null if not trashed.
export function daysUntilPurge(order) {
  if (!order?.trashedAt) return null;
  const trashedMs = order.trashedAt?.toMillis?.() ?? order.trashedAt?.seconds * 1000;
  if (!trashedMs) return TRASH_RETENTION_DAYS;
  const ageDays = (Date.now() - trashedMs) / DAY_MS;
  return Math.max(0, Math.ceil(TRASH_RETENTION_DAYS - ageDays));
}

// Load all currently-trashed orders for the Trash accordion.
export async function listTrashedOrders() {
  const snap = await getDocs(query(collection(db, "orders"), where("trashedAt", "!=", null)));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const am = a.trashedAt?.toMillis?.() ?? 0;
      const bm = b.trashedAt?.toMillis?.() ?? 0;
      return bm - am; // newest-trashed first
    });
}
